import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import useSWR from 'swr';
import { Badge, Button } from '@tremor/react';
import { Menu, MenuButton, MenuItems, MenuItem } from '@headlessui/react';
import { api } from '../lib/api';
import type {
  BundlesResponse,
  ClassifierBundle,
  BundleClassifierEntry,
  ResolvedBundle,
  ResolvedBundleClassifier,
  RegistryClassifiersResponse,
} from '../types';
import { NewBundleModal } from '../components/modals/NewBundleModal';
import { DuplicateBundleModal } from '../components/modals/DuplicateBundleModal';
import { ImportRegistryModal } from '../components/modals/ImportRegistryModal';

// ============================================================================
// Bundle Detail Page — /bundles/:key
// ============================================================================

// Editor for a single classifier entry's label exclusions + display name overrides
function ClassifierEntryEditor({
  classifierKey,
  entry,
  onUpdate,
}: {
  classifierKey: string;
  entry: BundleClassifierEntry;
  onUpdate: (updated: BundleClassifierEntry) => void;
}) {
  // Fetch the version to get the full label list + default display names
  const versionId = entry.classifierVersion || undefined;
  const { data: versions } = useSWR(
    `/api/registry/classifiers/${classifierKey}/versions`,
    () => api.getClassifierVersions(classifierKey),
    { refreshInterval: 0 }
  );

  const { data: classifierData } = useSWR(
    `/api/registry/classifiers/${classifierKey}`,
    () => api.getRegistryClassifier(classifierKey),
    { refreshInterval: 0 }
  );

  const familyDefaultVersion = classifierData?.currentVersion;
  const currentVersionId = versionId || familyDefaultVersion;
  const currentVersion = versions?.versions.find(v => v.version === currentVersionId);
  // Bundles operate on the effective label surface (post-override). Compute it
  // here so excludeLabels and labelDisplayNameOverrides line up with what the
  // classifier actually emits at runtime.
  const rawLabels = currentVersion?.labels || [];
  const versionOverrides = currentVersion?.labelOverrides || {};
  const allLabels = Array.from(
    new Set(rawLabels.map(l => versionOverrides[l] ?? l))
  );
  const excludeSet = new Set(entry.excludeLabels || []);
  const overrides = entry.labelDisplayNameOverrides || {};

  const setPinnedVersion = (value: string) => {
    // Empty string = clear pin and follow the family's currentVersion. Excluded
    // labels are version-specific (a label may not exist in another version),
    // so reset them when the pin changes to avoid stale entries.
    const next: BundleClassifierEntry = { ...entry, excludeLabels: [], labelDisplayNameOverrides: {} };
    if (value) next.classifierVersion = value;
    else delete next.classifierVersion;
    onUpdate(next);
  };

  const toggleLabel = (label: string) => {
    const newExclude = excludeSet.has(label)
      ? entry.excludeLabels.filter(l => l !== label)
      : [...entry.excludeLabels, label];
    onUpdate({ ...entry, excludeLabels: newExclude });
  };

  const excludeAll = () => {
    onUpdate({ ...entry, excludeLabels: [...allLabels] });
  };

  const includeAll = () => {
    onUpdate({ ...entry, excludeLabels: [] });
  };

  const updateDisplayName = (label: string, value: string) => {
    const newOverrides = { ...overrides };
    if (value === '' || value === label) {
      delete newOverrides[label];
    } else {
      newOverrides[label] = value;
    }
    onUpdate({ ...entry, labelDisplayNameOverrides: newOverrides });
  };

  const versionList = versions?.versions || [];

  const VersionPicker = (
    <div className="flex items-center gap-2 mb-3">
      <label className="text-xs text-gray-500 uppercase tracking-wide">Version</label>
      <select
        value={entry.classifierVersion || ''}
        onChange={(e) => setPinnedVersion(e.target.value)}
        className="text-xs px-2 py-1 rounded border border-gray-700 bg-gray-800 text-gray-200 focus:outline-none focus:ring-1 focus:ring-blue-500"
      >
        <option value="">
          Follow family default{familyDefaultVersion ? ` (${familyDefaultVersion})` : ''}
        </option>
        {versionList.map(v => (
          <option key={v.version} value={v.version}>
            {v.version} ({v.backend}{v.mode === 'prompt' ? ' • prompt' : ''}{v.status === 'deprecated' ? ' • deprecated' : ''})
          </option>
        ))}
      </select>
      {entry.classifierVersion ? (
        <span className="text-xs text-blue-400">pinned</span>
      ) : (
        <span className="text-xs text-gray-500">not pinned · resolves to family's current default</span>
      )}
    </div>
  );

  if (allLabels.length === 0) {
    return (
      <>
        {VersionPicker}
        <p className="text-xs text-gray-500 italic">Loading labels...</p>
      </>
    );
  }

  const includedCount = allLabels.length - excludeSet.size;
  const overrideCount = Object.keys(overrides).length;

  return (
    <div className="space-y-3">
      {VersionPicker}
      <div>
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs text-gray-500">
            {includedCount} of {allLabels.length} labels included
            {overrideCount > 0 && (
              <span className="ml-2 text-blue-400">
                · {overrideCount} display name override{overrideCount === 1 ? '' : 's'}
              </span>
            )}
          </p>
          <div className="flex gap-2">
            <button onClick={includeAll} className="text-xs text-blue-400 hover:text-blue-300">
              Include all
            </button>
            <button onClick={excludeAll} className="text-xs text-gray-400 hover:text-gray-300">
              Exclude all
            </button>
          </div>
        </div>
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-800 text-left">
              <th className="py-1 pr-3 text-xs font-medium text-gray-500 uppercase w-20">Included</th>
              <th className="py-1 pr-3 text-xs font-medium text-gray-500 uppercase w-1/3">Label</th>
              <th className="py-1 text-xs font-medium text-gray-500 uppercase">Display Name Override</th>
            </tr>
          </thead>
          <tbody>
            {allLabels.map(label => {
              const isExcluded = excludeSet.has(label);
              const override = overrides[label] || '';
              return (
                <tr key={label} className="border-b border-gray-800/30">
                  <td className="py-1.5 pr-3">
                    <button
                      type="button"
                      role="switch"
                      aria-checked={!isExcluded}
                      onClick={() => toggleLabel(label)}
                      title={isExcluded ? `Click to include '${label}'` : `Click to exclude '${label}'`}
                      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none focus:ring-1 focus:ring-blue-500 ${
                        isExcluded ? 'bg-gray-700 hover:bg-gray-600' : 'bg-blue-600 hover:bg-blue-500'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          isExcluded ? 'translate-x-0.5' : 'translate-x-4'
                        }`}
                      />
                    </button>
                  </td>
                  <td className="py-1.5 pr-3">
                    <code className={`text-xs font-mono ${isExcluded ? 'text-gray-600' : 'text-blue-300'}`}>
                      {label}
                    </code>
                  </td>
                  <td className="py-1.5">
                    <input
                      type="text"
                      value={override}
                      onChange={(e) => updateDisplayName(label, e.target.value)}
                      placeholder={label}
                      disabled={isExcluded}
                      className={`w-full text-xs px-2 py-1 rounded border border-gray-700 bg-gray-800 text-gray-200 placeholder-gray-600 focus:outline-none focus:ring-1 focus:ring-blue-500 ${
                        isExcluded ? 'opacity-40 cursor-not-allowed' : ''
                      }`}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function BundleDetailPage() {
  const { key } = useParams<{ key: string }>();
  const navigate = useNavigate();
  const [togglingActive, setTogglingActive] = useState(false);
  const [settingDefault, setSettingDefault] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editClassifiers, setEditClassifiers] = useState<BundleClassifierEntry[]>([]);
  const [editVisibility, setEditVisibility] = useState<'public' | 'private'>('public');
  const [saving, setSaving] = useState(false);
  const [showAddClassifier, setShowAddClassifier] = useState(false);
  const [duplicateOpen, setDuplicateOpen] = useState(false);

  const { data: bundle, mutate: mutateBundle } = useSWR<ClassifierBundle>(
    key ? `/api/registry/bundles/${key}` : null,
    () => api.getBundle(key!),
    { refreshInterval: 0 }
  );

  const { data: resolved, error, isLoading, mutate: mutateResolved } = useSWR<ResolvedBundle>(
    key && !editing ? `/api/registry/bundles/${key}/resolve` : null,
    () => api.resolveBundle(key!),
    { refreshInterval: 0 }
  );

  // All available classifiers (for the add classifier picker)
  const { data: allClassifiers } = useSWR<RegistryClassifiersResponse>(
    editing ? '/api/registry/classifiers?active=true' : null,
    () => api.getRegistryClassifiers(true),
    { refreshInterval: 0 }
  );

  const handleToggleActive = async () => {
    if (!bundle) return;
    setTogglingActive(true);
    try {
      if (bundle.isActive) {
        await api.deleteBundle(key!);
      } else {
        await api.updateBundle(key!, { isActive: true } as Partial<ClassifierBundle>);
      }
      await mutateBundle();
      await mutateResolved();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to update bundle');
    } finally {
      setTogglingActive(false);
    }
  };

  const handleSetDefault = async () => {
    setSettingDefault(true);
    try {
      await api.setBundleDefault(key!);
      await mutateBundle();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to set default');
    } finally {
      setSettingDefault(false);
    }
  };

  const startEditing = () => {
    if (!bundle) return;
    setEditName(bundle.name);
    setEditDescription(bundle.description || '');
    setEditClassifiers(bundle.classifiers.map(c => ({ ...c, excludeLabels: [...c.excludeLabels] })));
    setEditVisibility(bundle.visibility || 'public');
    setEditing(true);
  };

  const cancelEditing = () => {
    setEditing(false);
    setEditClassifiers([]);
    setShowAddClassifier(false);
  };

  const saveEditing = async () => {
    if (!editName.trim()) {
      alert('Bundle name is required');
      return;
    }
    setSaving(true);
    try {
      await api.updateBundle(key!, {
        name: editName.trim(),
        description: editDescription.trim() || undefined,
        classifiers: editClassifiers,
        visibility: editVisibility,
      } as Partial<ClassifierBundle>);
      await mutateBundle();
      setEditing(false);
      setEditClassifiers([]);
      await mutateResolved();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const addClassifier = (classifierKey: string) => {
    if (editClassifiers.some(c => c.classifierKey === classifierKey)) return;
    setEditClassifiers([
      ...editClassifiers,
      { classifierKey, excludeLabels: [], labelDisplayNameOverrides: {} },
    ]);
    setShowAddClassifier(false);
  };

  const removeClassifier = (classifierKey: string) => {
    setEditClassifiers(editClassifiers.filter(c => c.classifierKey !== classifierKey));
  };

  const updateClassifierEntry = (classifierKey: string, updated: BundleClassifierEntry) => {
    setEditClassifiers(editClassifiers.map(c =>
      c.classifierKey === classifierKey ? updated : c
    ));
  };

  if (isLoading || !bundle) {
    return (
      <div className="max-w-[1200px] mx-auto">
        <div className="bg-gray-900 rounded-lg border border-gray-800 p-8 text-center">
          <span className="text-gray-500 animate-pulse">Loading bundle...</span>
        </div>
      </div>
    );
  }

  // Note: we don't full-page error out on resolve failure. The bundle metadata
  // (from getBundle) loads even when isActive=false, but resolveBundle 404s on
  // inactive bundles — so the enable toggle in the header must remain reachable.
  // Resolve errors are surfaced inline in the resolved-classifiers section below.

  // Available classifiers that aren't already in the bundle
  const availableClassifiers = allClassifiers?.classifiers.filter(
    c => !editClassifiers.some(ec => ec.classifierKey === c.key)
  ) || [];

  return (
    <div className="max-w-[1200px] mx-auto space-y-4">
      {/* Breadcrumb + Enabled toggle */}
      <div>
        <div className="flex items-center justify-between gap-4 mb-2">
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <Link to="/bundles" className="hover:text-gray-300">Bundles</Link>
            <span>/</span>
            <span className="text-gray-300">{key}</span>
          </div>
          <button
            onClick={handleToggleActive}
            disabled={togglingActive || editing}
            className="flex items-center gap-2 text-sm shrink-0"
            title={bundle.isActive ? 'Click to disable' : 'Click to enable'}
          >
            <span className={`text-xs ${bundle.isActive ? 'text-emerald-400' : 'text-gray-500'}`}>
              {bundle.isActive ? 'Enabled' : 'Disabled'}
            </span>
            <span
              className={`w-8 h-5 rounded-full relative transition-colors ${
                bundle.isActive ? 'bg-emerald-600' : 'bg-gray-700'
              } ${togglingActive ? 'opacity-50' : ''}`}
            >
              <span
                className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${
                  bundle.isActive ? 'left-3.5' : 'left-0.5'
                }`}
              />
            </span>
          </button>
        </div>

        {/* Title + actions */}
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-2 min-w-0">
            <h1 className="text-2xl font-bold text-white truncate">{bundle.name}</h1>
            {bundle.isDefault && (
              <Badge color="blue" size="xs">default</Badge>
            )}
            {bundle.visibility === 'private' && (
              <Badge color="amber" size="xs">private</Badge>
            )}
          </div>
          {!editing && (
            <div className="flex items-center gap-2 shrink-0">
              <Button size="sm" onClick={startEditing}>
                Edit Bundle
              </Button>
              <Menu as="div" className="relative">
                <MenuButton
                  className="h-9 px-3 inline-flex items-center justify-center rounded-md border border-gray-700 bg-gray-800 hover:bg-gray-700 text-gray-300 text-sm font-medium leading-none data-[open]:bg-gray-700 disabled:opacity-50"
                  aria-label="More actions"
                  title="More actions"
                  disabled={settingDefault}
                >
                  &#x22EF;
                </MenuButton>
                <MenuItems
                  anchor="bottom end"
                  className="z-50 mt-1 min-w-[180px] bg-gray-900 border border-gray-800 rounded-lg shadow-lg p-1 focus:outline-none"
                >
                  <MenuItem disabled={bundle.isDefault || !bundle.isActive || settingDefault}>
                    <button
                      onClick={handleSetDefault}
                      className="w-full text-left px-3 py-2 text-sm rounded text-gray-200 data-[focus]:bg-gray-800 data-[disabled]:opacity-40 data-[disabled]:cursor-not-allowed"
                    >
                      Set as Default
                    </button>
                  </MenuItem>
                  <MenuItem>
                    <button
                      onClick={() => setDuplicateOpen(true)}
                      className="w-full text-left px-3 py-2 text-sm rounded text-gray-200 data-[focus]:bg-gray-800"
                    >
                      Duplicate
                    </button>
                  </MenuItem>
                  <MenuItem>
                    <button
                      onClick={async () => {
                        try {
                          await api.exportBundle(key!);
                        } catch (err) {
                          alert(err instanceof Error ? err.message : 'Export failed');
                        }
                      }}
                      className="w-full text-left px-3 py-2 text-sm rounded text-gray-200 data-[focus]:bg-gray-800"
                    >
                      Export as JSON
                    </button>
                  </MenuItem>
                </MenuItems>
              </Menu>
            </div>
          )}
        </div>

        {/* Bundle key */}
        <div className="mt-1 text-xs text-gray-500 font-mono">({bundle.key})</div>

        {/* Description */}
        {bundle.description && (
          <p className="mt-2 text-sm text-gray-500">{bundle.description}</p>
        )}
      </div>

      {/* Edit mode */}
      {editing && (
        <div className="space-y-4">
          {/* Edit toolbar */}
          <div className="flex items-center justify-between bg-blue-950/30 border border-blue-900/50 rounded-lg px-4 py-3">
            <span className="text-sm text-blue-300">Editing bundle</span>
            <div className="flex gap-2">
              <Button size="xs" variant="secondary" onClick={cancelEditing} disabled={saving}>
                Cancel
              </Button>
              <Button size="xs" onClick={saveEditing} loading={saving}>
                Save Changes
              </Button>
            </div>
          </div>

          {/* Name + description */}
          <div className="bg-gray-900 border border-gray-800 rounded-lg p-4 space-y-3">
            <div>
              <label className="text-xs text-gray-400 uppercase tracking-wide">Name</label>
              <input
                type="text"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                className="mt-1 w-full text-sm px-3 py-2 rounded border border-gray-700 bg-gray-800 text-gray-200 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="text-xs text-gray-400 uppercase tracking-wide">Description</label>
              <input
                type="text"
                value={editDescription}
                onChange={(e) => setEditDescription(e.target.value)}
                placeholder="What this bundle is used for"
                className="mt-1 w-full text-sm px-3 py-2 rounded border border-gray-700 bg-gray-800 text-gray-200 placeholder-gray-600 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="text-xs text-gray-400 uppercase tracking-wide">Visibility</label>
              <div className="mt-1 inline-flex rounded border border-gray-700 bg-gray-800 p-0.5 gap-0.5">
                <button
                  type="button"
                  onClick={() => setEditVisibility('public')}
                  className={`px-3 py-1.5 text-sm rounded ${editVisibility === 'public' ? 'bg-blue-900/60 text-blue-100' : 'text-gray-400 hover:text-gray-200'}`}
                >
                  Public
                </button>
                <button
                  type="button"
                  onClick={() => setEditVisibility('private')}
                  className={`px-3 py-1.5 text-sm rounded ${editVisibility === 'private' ? 'bg-blue-900/60 text-blue-100' : 'text-gray-400 hover:text-gray-200'}`}
                >
                  Private
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Public bundles show for public-only orgs and personal keys. Private ones are hidden there — reachable only via an org allowlist grant, a denylist org, or an admin. (Org allowlist/denylist on the Org page overrides this.)
              </p>
            </div>
          </div>

          {/* Classifier entries */}
          <div className="space-y-3">
            <h2 className="text-sm text-gray-400 uppercase tracking-wide">
              Classifiers ({editClassifiers.length})
            </h2>

            {editClassifiers.map(entry => (
              <div key={entry.classifierKey} className="bg-gray-900 border border-gray-800 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Link
                      to={`/classifiers/${entry.classifierKey}`}
                      className="text-sm font-semibold text-white hover:text-blue-300"
                    >
                      {entry.classifierKey}
                    </Link>
                    {entry.classifierVersion && (
                      <span className="text-xs text-gray-500 font-mono">
                        pinned: {entry.classifierVersion}
                      </span>
                    )}
                  </div>
                  <button
                    onClick={() => removeClassifier(entry.classifierKey)}
                    className="text-xs text-red-400 hover:text-red-300"
                  >
                    Remove
                  </button>
                </div>

                <ClassifierEntryEditor
                  classifierKey={entry.classifierKey}
                  entry={entry}
                  onUpdate={(updated) => updateClassifierEntry(entry.classifierKey, updated)}
                />
              </div>
            ))}

            {/* Add classifier */}
            {showAddClassifier ? (
              <div className="bg-gray-900 border border-dashed border-gray-700 rounded-lg p-4">
                <p className="text-xs text-gray-400 uppercase mb-2">Add a classifier</p>
                {availableClassifiers.length === 0 ? (
                  <p className="text-xs text-gray-500">All classifiers are already in this bundle.</p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {availableClassifiers.map(c => (
                      <button
                        key={c.key}
                        onClick={() => addClassifier(c.key)}
                        className="text-xs px-3 py-1.5 rounded bg-gray-800 text-gray-300 hover:bg-gray-700 hover:text-white transition-colors"
                      >
                        + {c.name} <span className="text-gray-500">({c.key})</span>
                      </button>
                    ))}
                  </div>
                )}
                <button
                  onClick={() => setShowAddClassifier(false)}
                  className="text-xs text-gray-500 hover:text-gray-400 mt-2"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <button
                onClick={() => setShowAddClassifier(true)}
                className="w-full border border-dashed border-gray-700 rounded-lg p-3 text-sm text-gray-500 hover:text-gray-300 hover:border-gray-600 transition-colors"
              >
                + Add Classifier
              </button>
            )}
          </div>
        </div>
      )}

      {/* Resolve error / disabled-bundle notice (when not editing and no resolved data) */}
      {!editing && !resolved && error && (
        <div className={`rounded-lg p-4 border ${
          bundle.isActive
            ? 'bg-red-950/50 border-red-900 text-red-400'
            : 'bg-yellow-950/30 border-yellow-900/50 text-yellow-300'
        }`}>
          {bundle.isActive ? (
            <p className="text-sm">Error loading resolved bundle: {error.message}</p>
          ) : (
            <p className="text-sm">
              This bundle is disabled, so its classifiers can't be resolved. Use the Enabled toggle above to re-enable it.
            </p>
          )}
        </div>
      )}

      {/* Read-only resolved view (when not editing) */}
      {!editing && resolved && (
        <>
          {/* Summary */}
          <div className="flex gap-4 text-sm text-gray-500">
            <span>{resolved.classifierCount} classifiers</span>
            <span>{resolved.labelCount} labels surfaced</span>
          </div>

          {/* Resolved classifiers */}
          <div className="space-y-3">
            <h2 className="text-sm text-gray-400 uppercase tracking-wide">Classifiers in this Bundle</h2>

            {resolved.classifiers.length === 0 ? (
              <div className="bg-gray-900 rounded-lg border border-gray-800 p-8 text-center">
                <p className="text-gray-500">No classifiers configured yet.</p>
                <Button size="sm" className="mt-3" onClick={startEditing}>
                  Edit Bundle
                </Button>
              </div>
            ) : (
              <div className="space-y-2">
                {resolved.classifiers.map((c) => (
                  <ResolvedClassifierRow key={c.classifierKey} classifier={c} />
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {bundle && (
        <DuplicateBundleModal
          isOpen={duplicateOpen}
          onClose={() => setDuplicateOpen(false)}
          source={bundle}
          onSuccess={(newKey) => navigate(`/bundles/${newKey}`)}
        />
      )}
    </div>
  );
}

function ResolvedClassifierRow({ classifier: c }: { classifier: ResolvedBundleClassifier }) {
  const [expanded, setExpanded] = useState(false);

  if (c.error) {
    return (
      <div className="bg-red-950/30 border border-red-900/50 rounded-lg px-4 py-3">
        <div className="flex items-center gap-2">
          <span className="text-sm font-mono text-red-400">{c.classifierKey}</span>
          <span className="text-xs text-red-500">{c.error}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-900 border border-gray-800 rounded-lg overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full px-4 py-3 text-left hover:bg-gray-800/30 transition-colors"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Link
              to={`/classifiers/${c.classifierKey}`}
              onClick={(e) => e.stopPropagation()}
              className="text-sm font-semibold text-white hover:text-blue-300"
            >
              {c.classifierName}
            </Link>
            <Link
              to={`/classifiers/${c.classifierKey}/${encodeURIComponent(c.version)}`}
              onClick={(e) => e.stopPropagation()}
              className="text-xs font-mono text-blue-400 hover:text-blue-300 hover:underline"
            >
              {c.version}
            </Link>
            {c.mode === 'prompt' && <Badge color="green" size="xs">prompt</Badge>}
          </div>
          <div className="flex items-center">
            <div className="w-20 flex justify-end">
              <Badge
                color={c.backend === 'openai' ? 'blue' : c.backend === 'anthropic' ? 'orange' : 'purple'}
                size="xs"
              >
                {c.backend}
              </Badge>
            </div>
            <span className="text-xs text-gray-500 w-40 text-right ml-1">
              {c.includedLabels.length} included
              {c.excludedLabels.length > 0 && (
                <span className="text-gray-600"> / {c.excludedLabels.length} excluded</span>
              )}
            </span>
            <span className="text-gray-600 text-xs ml-3">{expanded ? '▾' : '▸'}</span>
          </div>
        </div>
      </button>

      {expanded && (
        <div className="px-4 pb-3 border-t border-gray-800 pt-3">
          {/* Included labels with display names */}
          <div className="mb-3">
            <p className="text-xs text-gray-500 uppercase mb-1.5">Included Labels</p>
            <table className="w-full">
              <tbody>
                {c.includedLabels.map(label => (
                  <tr key={label} className="border-b border-gray-800/30">
                    <td className="py-1 pr-4">
                      <code className="text-xs font-mono text-blue-300">{label}</code>
                    </td>
                    <td className="py-1 text-xs text-gray-400">
                      {c.displayNames[label] !== label ? c.displayNames[label] : ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Excluded labels */}
          {c.excludedLabels.length > 0 && (
            <div>
              <p className="text-xs text-gray-500 uppercase mb-1.5">Excluded Labels</p>
              <div className="flex flex-wrap gap-1">
                {c.excludedLabels.map(label => (
                  <span key={label} className="text-xs px-2 py-0.5 rounded font-mono bg-gray-800 text-gray-500">
                    {label}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Bundle List Page — /bundles
// ============================================================================

function BundleRow({ bundle }: { bundle: ClassifierBundle }) {
  const classifierCount = bundle.classifiers.length;

  return (
    <Link
      to={`/bundles/${bundle.key}`}
      className={`block bg-gray-900 rounded-lg border overflow-hidden transition-colors cursor-pointer ${
        bundle.isActive ? 'border-gray-800 hover:border-gray-600' : 'border-gray-800/50'
      }`}
    >
      <div className="px-4 py-3">
        <div className="flex items-center justify-between gap-4">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <h2 className={`text-base font-semibold ${bundle.isActive ? 'text-white' : 'text-gray-500'}`}>
                {bundle.name}
              </h2>
              {bundle.isDefault && (
                <Badge color="blue" size="xs">default</Badge>
              )}
              {bundle.visibility === 'private' && (
                <Badge color="amber" size="xs">private</Badge>
              )}
              {!bundle.isActive && (
                <Badge color="gray" size="xs">disabled</Badge>
              )}
            </div>
            {bundle.description && (
              <p className="mt-1 text-xs text-gray-500 truncate">{bundle.description}</p>
            )}
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <span className="text-xs text-gray-500">{classifierCount} classifiers</span>
            <span className="text-gray-600 text-sm">&rarr;</span>
          </div>
        </div>
      </div>
    </Link>
  );
}

export function BundlesPage() {
  const navigate = useNavigate();
  const [showNewBundle, setShowNewBundle] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [exporting, setExporting] = useState(false);

  const { data, error, isLoading, mutate } = useSWR<BundlesResponse>(
    '/api/registry/bundles',
    () => api.getBundles(),
    { refreshInterval: 0 }
  );

  const handleExportAll = async () => {
    setExporting(true);
    try {
      await api.exportRegistry();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="max-w-[1200px] mx-auto">
        <div className="bg-gray-900 rounded-lg border border-gray-800 p-8">
          <div className="animate-pulse flex items-center justify-center">
            <span className="text-gray-500">Loading bundles...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-[1200px] mx-auto">
        <div className="bg-red-950/50 border border-red-900 rounded-lg p-4">
          <p className="text-red-400">Error loading bundles: {error.message}</p>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const activeCount = data.bundles.filter(b => b.isActive).length;

  return (
    <div className="max-w-[1200px] mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Classifier Bundles</h1>
          <p className="text-gray-400 mt-1">
            Configure which classifiers and labels to use for each use case
          </p>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-sm text-gray-500">
            {activeCount} active / {data.count} total
          </span>
          <Button size="sm" variant="secondary" onClick={handleExportAll} loading={exporting}>
            Export All
          </Button>
          <Button size="sm" variant="secondary" onClick={() => setShowImport(true)}>
            Import
          </Button>
          <Button size="sm" onClick={() => setShowNewBundle(true)}>
            + New Bundle
          </Button>
        </div>
      </div>

      {/* Bundle List */}
      <div className="space-y-2">
        {data.bundles.map(bundle => (
          <BundleRow key={bundle.key} bundle={bundle} />
        ))}
      </div>

      {data.bundles.length === 0 && (
        <div className="bg-gray-900 rounded-lg border border-gray-800 p-8 text-center">
          <p className="text-gray-500">No bundles configured.</p>
          <p className="text-gray-600 text-sm mt-1">
            Run <code className="text-gray-400">node scripts/seed-bundles.js</code> to create the default bundle, or create one manually.
          </p>
        </div>
      )}

      <NewBundleModal
        isOpen={showNewBundle}
        onClose={() => setShowNewBundle(false)}
        onSuccess={(newKey) => {
          mutate();
          navigate(`/bundles/${newKey}`);
        }}
      />

      <ImportRegistryModal
        isOpen={showImport}
        onClose={() => setShowImport(false)}
        onSuccess={() => mutate()}
      />
    </div>
  );
}
